<template>
    <div class="system-progression">
        <page-header :text="$t('navigation.systems.progression')" />

        <page-subnavigation :items="allSystemLinks" />

        <div class="brick-zero brick-wrapper--large">
            <div class="row brick-mobile--small--bottom divider divider--bottom">
                <div class="column-tablet-8">
                    <h6 class="brick-mobile--tiny--top brick-mobile--small--left">
                        {{ $t('systems.progression.display_period') }}
                    </h6>
                </div>
                <div class="column-tablet-16 brick-mobile--small--left">
                    <div class="form__row">
                        <div class="calendar-container">
                            <datepicker v-model="date.start" name="date_start" input-class="form__field form__field--short" :disabledDates="date.disabled"></datepicker>
                            <datepicker v-model="date.end" name="date_end" input-class="form__field form__field--short" :disabledDates="date.disabled"></datepicker>
                        </div>
                        <i class="icon icon--common-spinner spinner" :class="{ 'spinner--finished': loading == false }"></i>
                    </div>
                </div>
            </div>

            <div class="row brick-mobile--tiny--bottom brick-mobile--small--top divider divider--bottom">
                <div class="column-tablet-8">
                    <h6 class="brick-mobile--small--left">
                        {{ $t('systems.progression.modules') }}
                    </h6>
                </div>
                <div class="column-tablet-16 brick-mobile--small--left">
                    <div v-for="datapoint in moduleDatapoints" :key="datapoint.u_id" class="brick-mobile--small--right brick-mobile--tiny--bottom">
                        <input type="checkbox" v-model="datapoint.selected" :id="datapoint.u_id" value="1" class="form__checkbox" @change="toggleModuleData(datapoint)" />
                        <label :for="datapoint.u_id" class="form__checkbox__label">
                            {{ $t('oids.' + datapoint.oid) }}
                            <span v-if="datapoint.function_name">({{ datapoint.function_name }})</span>
                        </label>
                    </div>
                </div>
            </div>
            <div class="brick-mobile--small--top brick-mobile--small--bottom">
                <div class="table-wrapper system-progression__table-wrapper">
                    <table class="table table--striped-decent">
                        <thead>
                            <tr>
                                <th class="table__data table__data--8">
                                    {{ $t('systems.progression.boiler') }}
                                </th>
                                <th v-for="boiler in uniqueBoilerDatapoints" :key="boiler.u_id" class="table__data table__data--min nowrap align-center">
                                    {{ boiler.function_name }}
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(nodes, oid) in oidBoilerMap" :key="oid">
                                <td>{{ $t('oids.' + oid) }}</td>
                                <td v-for="boiler in uniqueBoilerDatapoints" :key="boiler.u_id" class="align-center">
                                    <input
                                        type="checkbox"
                                        v-if="nodes[boiler.functionNodeId]"
                                        v-model="nodes[boiler.functionNodeId].selected"
                                        id="param2"
                                        name="param2"
                                        value="1"
                                        class="form__checkbox"
                                        @change="toggleModuleData(nodes[boiler.functionNodeId])"
                                    />
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div id="table_div"></div>

            <div class="brick-mobile--top">
                <h2>{{ $t('navigation.systems.progression') }}</h2>

                <div class="chart system-progression__chart" id="progression-chart">
                    <GChart type="LineChart" :data="chartData" :options="chart.options" :settings="{ packages: ['corechart'] }" @ready="highlightDays()" />
                </div>
            </div>

            <div class="brick-mobile--top">
                <a v-if="helperDataAvailable" @click="downloadCSV()" class="button button--primary">Download</a>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import { GChart } from 'vue-google-charts';
import * as moment from 'moment';
import { mapState } from 'vuex';
import sortBy from 'lodash/sortBy';
import forEach from 'lodash/forEach';
import fill from 'lodash/fill';
import uniqueId from 'lodash/uniqueId';
import uniqBy from 'lodash/uniqBy';
import Datepicker from 'vuejs-datepicker';
import { startCSVDownload } from '@/utils/csvExport';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    components: {
        PageHeader,
        Datepicker,
        GChart,
        PageSubnavigation,
    },
    mixins: [generalLinks],
    data() {
        return {
            helperData: {},
            date: {
                start: moment()
                    .subtract(1, 'day')
                    .startOf('day')
                    .toDate(),
                end: moment()
                    .endOf('day')
                    .toDate(),
                disabled: {
                    from: moment()
                        .endOf('day')
                        .toDate(),
                },
            },
            loading: false,
            oids: [],
            datapoints: false,
            moduleDatapoints: [],
            moduleData: [],
            boilerDatapoints: [],
            uniqueBoilerDatapoints: [],
            boilerData: [],
            oidBoilerMap: {},
            chart: {
                options: {
                    annotations: {
                        domain: {
                            style: 'line',
                            stemColor: 'black',
                        },
                    },
                    chartArea: {
                        width: '90%',
                        height: '75%',
                        left: 50,
                        top: '5%',
                    },
                    fontSize: 12,
                    hAxis: {
                        baselineColor: '#555555',
                        gridlines: {
                            count: -1,
                            color: '#aaaaaa',
                            units: {
                                days: {
                                    format: ['dd.MM.'],
                                },
                                hours: {
                                    format: ['HH:mm'],
                                },
                            },
                        },
                        minorGridlines: {
                            color: '#d9d9d9',
                            units: {
                                days: {
                                    format: ['dd.MM.'],
                                },
                                hours: {
                                    format: ['HH:mm'],
                                },
                            },
                        },
                        textStyle: {
                            color: '#555555',
                        },
                    },
                    height: 350,
                    interpolateNulls: true,
                    legend: {
                        position: 'bottom',
                        alignment: 'start',
                        scrollArrows: {
                            activeColor: '#555555',
                        },
                        pagingTextStyle: {
                            color: '#555555',
                        },
                    },
                    tooltip: {
                        isHtml: true,
                    },
                    vAxis: {
                        baselineColor: '#555555',
                        format: '###.##',
                        gridlines: {
                            color: '#d9d9d9',
                        },
                        textStyle: {
                            color: '#555555',
                        },
                    },
                },
            },
        };
    },
    created() {
        if (this.activeSystem !== null) this.fetchProgression();
    },
    computed: {
        ...mapState(['activeSystem']),
        chartData() {
            if (this.helperDataAvailable) {
                if (this.chart.options.series) delete this.chart.options.series;
                this.chart.options.tooltip = { isHtml: true };
                return this.createChartData();
            } else {
                this.chart.options.series = {
                    0: {
                        visibleInLegend: false,
                        color: 'transparent',
                    },
                };
                this.chart.options.tooltip.trigger = 'none';
                return this.createEmptyChartData();
            }
        },
        helperDataAvailable() {
            return Object.keys(this.helperData).length !== 0;
        },
    },
    methods: {
        highlightDays() {
            const dateElems = document.querySelectorAll('#progression-chart g g text');
            dateElems.forEach(e => {
                const le = e.textContent.split('.').length - 1;

                if (le >= 2) {
                    e.style.fill = 'black';
                    e.style.fontWeight = '700';
                }
            });

            // const dateLines = document.querySelectorAll('#progression-chart g g rect');
            // dateLines.forEach(l => {
            //     if(l.getAttribute('fill') === '#aaaaaa'){
            //         l.style.fill = 'black';
            //     }
            // });
        },
        fetchProgression() {
            api.systems.datapoints({ id: this.activeSystem.id }).then(response => {
                this.datapoints = response.data.datapoints;
                // Sort all data points
                sortBy(this.datapoints, ['node_id', 'oid']);

                this.getModuleDatapoints();
                this.getBoilerDatapoints();
            });
        },
        fetchDatapoint(datapoint) {
            let params = {
                id: this.activeSystem.id,
                nodeId: datapoint.node_id,
                functionId: datapoint.function_id,
                oid: datapoint.oid,
                from: (this.date.start / 1000).toFixed(0),
                to: (this.date.end / 1000).toFixed(0),
            };

            // get data for specific datapoint and save it to helper array
            api.systems.datapointHistory(params).then(res => {
                let tempData = Object.assign({}, this.helperData);
                let valuesArrays = res.data.values;
                let name = `${datapoint.selector_name} (${datapoint.function_name})`;

                tempData[name] = {};
                tempData[name]['timevalues'] = {};

                tempData[name]['options'] = {
                    type: this.getOidType(datapoint.oid),
                    color: this.getOidColor(datapoint.oid),
                };

                tempData[name]['info'] = {
                    name: name,
                    node: datapoint.node_name,
                };

                forEach(valuesArrays, (array, index) => {
                    let timestamp = array[0];
                    let value = array[1];
                    tempData[name]['timevalues'][timestamp] = value;
                });
                this.helperData = tempData;
            });
        },
        getModuleDatapoints() {
            // Filter only module data points (e.g. KAS, UML, ...)
            forEach(this.datapoints, (value, key) => {
                if (!this.isBoiler(value.node_id)) {
                    value.u_id = uniqueId('datapoint_');
                    value.selected = false;
                    value.selector_name = this.$t('oids.' + value.oid);
                    if (value.function_name) value.selector_name += ` (${value.function_name})`;
                    this.moduleDatapoints.push(value);
                }
            });
        },
        getBoilerDatapoints() {
            // Filter only boiler data points (e.g. BioWIN)
            forEach(this.datapoints, (value, key) => {
                if (this.isBoiler(value.node_id)) {
                    value.u_id = uniqueId('datapoint_');
                    value.selector_name = this.$t('oids.' + value.oid);
                    value.selected = false;
                    value.functionNodeId = value.function_id + '_' + value.node_id;
                    this.boilerDatapoints.push(value);

                    // Create a map for the boiler OIDs
                    if (value.oid != '2/59') {
                        this.oidBoilerMap[value.oid] = this.oidBoilerMap[value.oid] ? this.oidBoilerMap[value.oid] : {};
                        this.oidBoilerMap[value.oid][value.functionNodeId] = value;
                    }
                }
            });

            //  sort oidBoilerMap
            this.oidBoilerMap = Object.entries(this.oidBoilerMap)
                .sort(([, a], [, b]) => {
                    const keyA = Object.keys(a)[0];
                    const keyB = Object.keys(b)[0];
                    return keyA.split('_')[0] - keyB.split('_')[0] || keyA.split('_')[1] - keyB.split('_')[1];
                })
                .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

            // Get unique boiler data points (for the headline of the table) and sort them
            this.boilerDatapoints = this.boilerDatapoints.sort((a, b) => a.function_id - b.function_id || a.node_id - b.node_id);
            this.uniqueBoilerDatapoints = uniqBy(this.boilerDatapoints, 'functionNodeId');
        },
        isBoiler(id) {
            return (id >= 55 && id <= 69) || (id >= 90 && id <= 93);
        },
        getOidType(oid) {
            let units = {
                default: 'degree',
                '0/9': 'percent',
                '2/1': 'enum',
            };
            return units[oid] || units['default'];
        },
        getOidColor(oid) {
            let colors = {
                '1/7': '#1696cc', // Kesseltemperatur Soll
                '2/1': '#ff0000', // Betriebsphase
                '0/9': '#f0ee1f', // Kesselleistung in %
                '0/7': '#de1212', // Kesseltemperatur IST in °C
                '0/0': '#28a846', // Aussentemperatur
                '21/65': '#222222', // Puffertemperatur
                '0/15': '#222222', // Puffertemperatur
            };

            return colors[oid];
        },
        toggleModuleData(datapoint) {
            this.chart.data = [];
            if (datapoint.selected && datapoint.selected !== undefined) {
                this.fetchDatapoint(datapoint);
            } else {
                let tempData = Object.assign({}, this.helperData);
                delete tempData[`${datapoint.selector_name} (${datapoint.function_name})`];
                this.helperData = tempData;
            }
        },
        createChartData() {
            let data = [];
            let header = ['Timestamp'];
            let headerEnum = [];
            let headerOther = [];
            let index = 0;
            let hasEnum = false;

            forEach(this.helperData, (values, key) => {
                if (values.options.type === 'enum' && headerEnum.length < 3) {
                    hasEnum = true;
                    headerEnum.push({ type: 'string', role: 'annotation', label: 'AV' });
                    headerEnum.push({
                        type: 'string',
                        role: 'annotationText',
                        label: 'AT',
                        p: { html: true },
                    });
                    headerEnum.push('Placeholder');
                } else if (values.options.type !== 'enum') {
                    headerOther.push(key);
                }
            });

            forEach(this.helperData, (values, key) => {
                let lastValue;

                forEach(values.timevalues, (value, timestamp) => {
                    if (values.options.type === 'enum' && lastValue === value) {
                        return;
                    }

                    let first = [new Date(timestamp * 1000)];
                    let enumRow = hasEnum ? new Array(3) : [];
                    let otherRow = new Array(headerOther.length);
                    fill(otherRow, null);

                    lastValue = value;

                    if (values.options.type === 'enum') {
                        enumRow[0] = this.getEnumLabel(value);
                        enumRow[1] = this.generateTooltipMarkup([key, timestamp, value]);
                        enumRow[2] = 0;
                    } else {
                        otherRow[index] = parseFloat(value);
                    }

                    let row = first.concat(enumRow, otherRow);

                    data.push(row);
                });

                if (values.options.type !== 'enum') {
                    index++;
                }
            });

            data = sortBy(data, row => {
                return row[0];
            });

            if (headerEnum.length > 0) {
                this.chart.options.series = {
                    0: {
                        visibleInLegend: false,
                        color: 'transparent',
                        tooltip: false,
                    },
                };
            }

            let fullHeader = header.concat(headerEnum, headerOther);
            data.unshift(fullHeader);

            return data;
        },
        createEmptyChartData() {
            let data = [];
            let header = [
                'Date',
                {
                    role: 'style',
                },
                'Value',
            ];
            let row = new Array(24);
            for (let i = 0; i <= 24; i++) {
                let row = [
                    moment()
                        .startOf('day')
                        .add(i, 'hours')
                        .toDate(),
                    'color: transparent, opacity: 0',
                    0,
                ];
                data.push(row);
            }
            data.unshift(header);
            return data;
        },
        getEnumLabel(enumValue) {
            return this.$t('enums.2/1.' + enumValue) || enumValue;
        },
        generateTooltipMarkup(items) {
            let markup = `
                <ul class="google-visualization-tooltip-item-list" style="">
                    <li class="google-visualization-tooltip-item" style="color:black; font-size:12px;">
                        <div style="margin-bottom: 10px;"><strong>${moment.unix(items[1]).format('MMM DD, YYYY, h:m:s A')}</strong></div>
                        <div>${items[0]}: <strong>${this.getEnumLabel(items[2])}</strong></div>
                    </li>
                </ul>
            `;

            return markup;
        },
        downloadCSV() {
            let csvOut = this.dataToCSV();
            startCSVDownload(csvOut);
        },
        dataToCSV() {
            let output = {};
            let prevTimestamp = undefined;
            let csv = '\uFEFF;';
            let i = 0;

            forEach(this.helperData, data => {
                csv += `${data.info.name} (${data.info.node});`;

                forEach(data.timevalues, (value, index) => {
                    if (!output[index]) output[index] = [];

                    if (data.options.type == 'enum') {
                        output[index][i] = this.getEnumLabel(value);
                    } else {
                        output[index][i] = String(value).replace('.', ',');
                    }
                });
                i++;
            });

            csv += '\r\n';

            forEach(output, (row, timestamp, collection) => {
                if (prevTimestamp && timestamp - prevTimestamp < 600) {
                    // add current values to prev values
                    forEach(row, (column, columnIndex) => {
                        if (column) {
                            collection[prevTimestamp][columnIndex] = column;
                        }
                    });

                    delete collection[timestamp];
                } else {
                    prevTimestamp = timestamp;
                }
            });

            forEach(output, (row, timestamp) => {
                csv += `${moment(timestamp, 'X').format('YYYY-MM-DD HH:mm:ss')};`;
                for (let b = 0; b < Object.keys(this.helperData).length; b++) {
                    csv += `${row[b] || ''};`;
                }

                csv += '\r\n';
            });

            return csv;
        },
    },
    watch: {
        'date.start'(newVal) {
            if (newVal > this.date.end) {
                this.date.start = moment(this.date.end)
                    .subtract(1, 'day')
                    .startOf('day')
                    .toDate();
                this.$notify({
                    type: 'error',
                    text: this.$t('systems.progression.error_start_end_date'),
                });
            } else {
                forEach(this.datapoints, datapoint => {
                    this.toggleModuleData(datapoint);
                });
            }
        },
        'date.end'(newVal) {
            if (newVal < this.date.start) {
                this.date.end = moment(this.date.start)
                    .add(1, 'day')
                    .startOf('day')
                    .toDate();
                this.$notify({
                    type: 'error',
                    text: this.$t('systems.progression.error_start_end_date'),
                });
            } else {
                forEach(this.datapoints, datapoint => {
                    this.toggleModuleData(datapoint);
                });
            }
        },
        chartData() {
            const that = this;
            setTimeout(function() {
                that.highlightDays();
            });
        },
        activeSystem(to, from) {
            if (from === null && to !== null) this.fetchProgression();
        },
    },
};
</script>

<style lang="scss">
.system-progression {
    &__table-wrapper {
        @include mq($until: wrapper) {
            margin-left: -25px;
            width: calc(100% + 50px);
            max-width: calc(100% + 50px);
        }
    }

    &__chart {
        @include mq($until: tablet) {
            overflow: scroll;
            margin-left: -25px;
            width: calc(100% + 50px);
        }

        & > div {
            @include mq($until: tablet) {
                min-width: 600px;
            }
        }
    }
}
.calendar-container {
    display: flex;

    @include mq($until: mobile-landscape) {
        flex-direction: column;
        row-gap: 4px;
    }

    input {
        @include mq($until: mobile-landscape) {
            width: 100%;
        }
    }
}
</style>
